import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Switch } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { PhotoLibraryOutlined, SortByAlpha } from '@mui/icons-material';
import ImageEditDialog, {
  BatchProcessedImageData,
} from '../../ImageEditDialog/index';
import { MediaResource } from '../../../../../system/media';
import { ObjectDetectionButton } from './ObjectDetectionButton';
import { FlattenedResults } from '../../hooks/useAIObjects';

// Utility function for getting file with lowest display order
const getFileWithLowestDisplayOrder = (
  files: MediaResource[],
): MediaResource | null => {
  if (!files || files.length === 0) {
    return null;
  }

  return files.reduce((lowest, current) => {
    return current.MEDIA_DISPLAY_ORDER < lowest.MEDIA_DISPLAY_ORDER
      ? current
      : lowest;
  });
};

interface PhotoActionsProps {
  // Photo management
  localFiles: MediaResource[];
  selectedFiles: string[];
  // Handlers
  onSortByName: () => void;
  onSelectAll: () => void;
  onDeleteSelected: () => void;
  onSaveProcessedImages: (batchData: BatchProcessedImageData) => void;
  // AI Object Detection
  objectDetectionResults: FlattenedResults;
  isPendingGeneration: () => boolean;
  pendingGenerations: string[];
  progress: { done: number; total: number };
  onOpenObjectPanel: () => void;
  onReanalyzePhotos: () => void;
  // Feature flags
  useObjectDetection: boolean;
  isEditImageEnabled: boolean;
  // Preferences
  isShowDescriptions: boolean;
  setIsShowDescriptions: (value: boolean) => void;
  // UI State
  isSortingDisabled: boolean;
  isSmallDisplay: boolean;
}

export const PhotoActions: React.FC<PhotoActionsProps> = ({
  localFiles,
  selectedFiles,
  onSortByName,
  onSelectAll,
  onDeleteSelected,
  onSaveProcessedImages,
  objectDetectionResults,
  isPendingGeneration,
  progress,
  onOpenObjectPanel,
  onReanalyzePhotos,
  useObjectDetection,
  isEditImageEnabled,
  isShowDescriptions,
  setIsShowDescriptions,
  isSortingDisabled,
  isSmallDisplay,
}) => {
  const [open, setOpen] = useState(false);

  const openEditDialog = () => {
    setOpen(true);
  };

  const closeEditDialog = () => {
    setOpen(false);
  };

  const handleSaveProcessedImages = async (
    batchData: BatchProcessedImageData,
  ) => {
    await onSaveProcessedImages(batchData);
    closeEditDialog();
  };

  return (
    <Box
      sx={{
        my: 2,
        display: 'flex',
        borderRadius: 1,
        boxShadow:
          '0 0 0 1px rgba(187, 187, 196, 0.50), 0 0 6px 0 rgba(0, 0, 0, 0.18)',
        flexDirection: { xs: 'column', sm: 'column', md: 'row' },
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-around',
          gap: 1,
          mb: { xs: 1, md: 0 },
        }}
      >
        {/* Sort By Name */}
        <Button
          variant="text"
          disabled={isSortingDisabled}
          onClick={onSortByName}
          startIcon={<SortByAlpha sx={{ color: 'text.secondary' }} />}
          sx={{
            maxWidth: isSmallDisplay ? 'none' : '200px',
            m: 1,
            p: '4px',
            fontSize: isSmallDisplay ? '1rem' : '14px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '16px',
            textTransform: 'capitalize',
            color: 'text.secondary',
          }}
        >
          Sort By Name
        </Button>

        {/* Photo Editor */}
        {isEditImageEnabled && (
          <>
            <Button
              variant="text"
              startIcon={<PhotoLibraryOutlined />}
              disabled={isSortingDisabled}
              onClick={openEditDialog}
              sx={{
                maxWidth: isSmallDisplay ? 'none' : '200px',
                m: 1,
                p: '4px',
                color: 'text.secondary',
                fontSize: isSmallDisplay ? '1rem' : '14px',
                fontStyle: 'normal',
                fontWeight: '500',
                lineHeight: '16px',
                textTransform: 'capitalize',
              }}
            >
              Photo Editor
            </Button>
            <ImageEditDialog
              selectedMediaRecord={
                getFileWithLowestDisplayOrder(localFiles) || localFiles[0]
              }
              open={open}
              onClose={closeEditDialog}
              onSave={handleSaveProcessedImages}
            />
          </>
        )}

        {/* Select All */}
        <Button
          variant="text"
          onClick={onSelectAll}
          startIcon={
            <Checkbox
              size="small"
              sx={{
                color: 'text.secondary',
                m: 0,
              }}
              checked={
                selectedFiles.length === localFiles.length &&
                localFiles.length > 0
              }
              indeterminate={
                selectedFiles.length > 0 &&
                selectedFiles.length < localFiles.length
              }
            />
          }
          sx={{
            color: 'text.secondary',
            fontSize: isSmallDisplay ? '1rem' : '14px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '24px',
            textTransform: 'capitalize',
            m: 1,
            p: '4px',
          }}
        >
          Select All
        </Button>

        {/* Delete Selected */}
        <Button
          sx={{
            display: selectedFiles.length === 0 ? 'none' : undefined,
            maxWidth: isSmallDisplay ? 'none' : '200px',
            pl: 0,
            color: selectedFiles.length > 0 ? 'error.medium' : 'text.secondary',
            fontSize: isSmallDisplay ? '1rem' : '14px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '24px',
            textTransform: 'capitalize',
            m: 1,
            p: '4px',
          }}
          disabled={selectedFiles.length === 0}
          onClick={onDeleteSelected}
          startIcon={<DeleteIcon />}
        >
          {selectedFiles.length === 0
            ? 'Delete Selected'
            : `Delete Selected (${selectedFiles.length})`}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'space-around', md: 'flex-end' },
          alignItems: 'center',
          gap: 2,
        }}
      >
        {useObjectDetection && (
          <ObjectDetectionButton
            isPendingGeneration={isPendingGeneration}
            progress={progress}
            objectDetectionResults={objectDetectionResults}
            onViewObjects={onOpenObjectPanel}
            onReanalyze={onReanalyzePhotos}
            isSmallDisplay={isSmallDisplay}
          />
        )}
        <FormControlLabel
          control={
            <Switch
              checked={isShowDescriptions}
              onChange={() => setIsShowDescriptions(!isShowDescriptions)}
              name="showDescriptions"
            />
          }
          label="Show Descriptions"
        />
      </Box>
    </Box>
  );
};
